import React, { useState, useEffect } from 'react';
import { X, Search, Pin, BarChart3, Clock, Trash } from 'lucide-react';
import MultiSelect from '@/components/ui/multi-select';
import SecondaryButton from "@/components/SecondaryButton";
import NeutralButton from '@/components/NeutralButton';
import SimpleMultiSelect from '@/components/ui/SimpleMultiSelect';
import { API_BASE_URL } from '../config';
import { formatMetricLabel } from '@/lib/utils';

const WatchlistDetailModal = ({ watchlistId, isOpen, onClose, onUpdateSuccessful, onDeleteSuccessful }) => {
  const toCsvString = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.map(v => (v === null || v === undefined ? '' : String(v))).join(',');
    if (typeof value === 'object') return '';
    return String(value);
  };

  const splitCsv = (value) => {
    const str = toCsvString(value);
    return str ? str.split(',') : [];
  };

  const [formData, setFormData] = useState({
    name: '',
    type: 'dynamic',
    description: '',
    criteria: [{ key: '', op: '', value: '' }],
    staticStocks: [],
    alertsEnabled: true
  });
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [metricMeta, setMetricMeta] = useState({});

  useEffect(() => {
    fetchAvailableMetrics();
  }, []);

  const fetchAvailableMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-group/filtering-metrics/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      setAvailableMetrics(result.data || []);
    } catch (err) {
      console.error('Failed to fetch available metrics:', err);
    }
  };

  const fetchMetricDetails = async (metric) => {
    if (!metric || metricMeta[metric]) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-group/filtering-metrics/?metric=${metric}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      setMetricMeta(prev => ({ ...prev, [metric]: result }));
    } catch (err) {
      console.error(`Failed to fetch details for metric ${metric}:`, err);
    }
  };

  const operators = [
    { value: 'gt', label: '>' },
    { value: 'lt', label: '<' },
    { value: 'gte', label: '>=' },
    { value: 'lte', label: '<=' },
    { value: 'equals', label: '=' },
    { value: 'not_equals', label: '!=' }
  ];

  useEffect(() => {
    if (isOpen && watchlistId) {
      fetchWatchlistDetails();
    }
  }, [isOpen, watchlistId]);

  const fetchWatchlistDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-groups/${watchlistId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      const criteriaRaw = data.is_dynamic ? (data.criteria || [{ key: '', op: '', value: '' }]) : [{ key: '', op: '', value: '' }];
      const criteria = (Array.isArray(criteriaRaw) ? criteriaRaw : [{ key: '', op: '', value: '' }]).map((c) => ({
        key: c?.key ?? '',
        op: c?.op ?? '',
        value: toCsvString(c?.value),
      }));
      
      // Fetch details for existing criteria metrics
      criteria.forEach(c => {
        if (c.key) fetchMetricDetails(c.key);
      });

      setFormData({
        name: data.group_name || '',
        type: data.is_dynamic ? 'dynamic' : 'static',
        description: data.description || '',
        criteria: criteria,
        alertsEnabled: data.analyst_prediction_alert ?? true,
        staticStocks: (data.symbols || []).map((id, index) => ({
          id: id,
          symbol: data.symbol_names ? data.symbol_names[index] : `ID: ${id}`,
          full_symbol: data.symbol_names ? data.symbol_names[index] : `ID: ${id}`
        }))
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleConditionChange = (index, field, value) => {
    const newCriteria = [...formData.criteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    
    if (field === 'key') {
      newCriteria[index].value = ''; // Reset value when key changes
      newCriteria[index].op = '';    // Reset operator when key changes
      fetchMetricDetails(value);
    }
    
    handleInputChange('criteria', newCriteria);
  };

  const addCondition = () => {
    handleInputChange('criteria', [
      ...formData.criteria,
      { key: '', op: '', value: '' },
    ]);
  };

  const removeCondition = (index) => {
    const newCriteria = formData.criteria.filter((_, i) => i !== index);
    handleInputChange('criteria', newCriteria);
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        group_name: formData.name,
        is_dynamic: formData.type === 'dynamic',
        description: formData.description,
        analyst_prediction_alert: formData.alertsEnabled,
      };

      if (formData.type === 'static') {
        payload.symbols = formData.staticStocks.map(s => (typeof s === 'object' ? s.id : s));
      } else {
        payload.criteria = formData.criteria;
      }

      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-groups/${watchlistId}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Please fill all fields properly and try again.');
      onUpdateSuccessful();
      onClose();
    } catch (err) {
      setError('Please fill all fields properly and try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this watchlist?')) return;
    setDeleteLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/screener/symbol-groups/${watchlistId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onDeleteSuccessful();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-900">
              Watchlist Details
            </h2>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${formData.type === 'static' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
              {formData.type.toUpperCase()}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <p className="text-gray-500">Loading details...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {error && <p className="text-red-500 text-sm">{error}</p>}
              
              {/* Watchlist Details Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Watchlist Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
                <div className='flex gap-2 items-center'>
                  <input
                    type='checkbox'
                    id='alertsEnabled'
                    checked={formData.alertsEnabled}
                    onChange={(e) => handleInputChange('alertsEnabled', e.target.checked)}
                  />
                  <label htmlFor='alertsEnabled'>Get analyst alerts for this watchlist</label>
                </div>
              </div>

              {/* Dynamic Criteria */}
              {formData.type === 'dynamic' && (
                <div className="space-y-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <Search size={18} className="text-gray-900" />
                    <h3 className="text-lg font-semibold text-gray-900">Screening Criteria</h3>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <div className="space-y-4">
                        {formData.criteria.map((condition, index) => {
                          const selectedMetric = availableMetrics.find(m => m.category === condition.key);
                          const isDate = selectedMetric?.datatype === 'Char' && selectedMetric?.category.toLowerCase().includes('date');
                          const meta = metricMeta[condition.key];
                          
                          // A metric is numeric if it's explicitly Decimal/Percentage, 
                          // OR if it's a Char field (non-date) that has a help_range but no options data.
                          const isNumeric = (selectedMetric?.datatype === 'Decimal' || selectedMetric?.datatype === 'Percentage') ||
                                           (selectedMetric?.datatype === 'Char' && !isDate && meta?.help_range && (!meta?.data || meta.data.length === 0));
                          
                          const isCategorical = selectedMetric?.datatype === 'Char' && !isDate && !isNumeric;

                          return (
                            <div key={index} className="grid grid-cols-12 gap-3 items-end mb-3 last:mb-0">
                              {/* Metric */}
                              <div className={condition.key ? "col-span-12 sm:col-span-4" : "col-span-11"}>
                                <select
                                  value={condition.key}
                                  onChange={(e) => handleConditionChange(index, 'key', e.target.value)}
                                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                  <option value="">Select field</option>
                                  {availableMetrics.map((f) => (
                                    <option key={f.category} value={f.category}>
                                      {formatMetricLabel(f.category)}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {condition.key && (
                                <>
                                  {/* Operator - Only for Numeric */}
                                  {isNumeric && (
                                    <div className="col-span-12 sm:col-span-2">
                                      <select
                                        value={condition.op}
                                        onChange={(e) => handleConditionChange(index, 'op', e.target.value)}
                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                      >
                                        <option value="">Op</option>
                                        {operators.map((op) => (
                                          <option key={op.value} value={op.value}>
                                            {op.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {/* Value - Responsive width based on Operator visibility */}
                                  <div className={`col-span-12 ${isNumeric ? 'sm:col-span-4' : 'sm:col-span-6'}`}>
                                    {isDate ? (
                                      <div className="space-y-1">
                                        {meta?.help_range && (
                                          <p className="text-[10px] text-gray-500 ml-1">
                                            Range: {meta.help_range.min} to {meta.help_range.max}
                                          </p>
                                        )}
                                        <div className="flex gap-2">
                                          <input
                                            type="date"
                                            value={splitCsv(condition.value)[0] || ''}
                                            onChange={(e) => {
                                              const dates = splitCsv(condition.value);
                                              handleConditionChange(index, 'value', `${e.target.value},${dates[1] || ''}`);
                                            }}
                                            className="w-1/2 px-2 py-2 bg-white border border-gray-300 rounded-lg text-xs"
                                          />
                                          <input
                                            type="date"
                                            value={splitCsv(condition.value)[1] || ''}
                                            onChange={(e) => {
                                              const dates = splitCsv(condition.value);
                                              handleConditionChange(index, 'value', `${dates[0] || ''},${e.target.value}`);
                                            }}
                                            className="w-1/2 px-2 py-2 bg-white border border-gray-300 rounded-lg text-xs"
                                          />
                                        </div>
                                      </div>
                                    ) : isCategorical ? (
                                      <SimpleMultiSelect
                                        value={splitCsv(condition.value)}
                                        options={meta?.data || []}
                                        onChange={(values) => handleConditionChange(index, 'value', values.join(','))}
                                        placeholder="Select options"
                                      />
                                    ) : (
                                      <div className="space-y-1">
                                        {isNumeric && meta?.help_range && (
                                          <p className="text-[10px] text-gray-500 ml-1">
                                            Range: {meta.help_range.min} to {meta.help_range.max}
                                          </p>
                                        )}
                                        <input
                                          type="text"
                                          value={condition.value}
                                          onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                                          placeholder="Value"
                                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}

                              {/* Remove */}
                              <div className={condition.key ? "col-span-12 sm:col-span-2 flex justify-end" : "col-span-1 flex justify-end"}>
                                {formData.criteria.length > 1 && (
                                  <button
                                    onClick={() => removeCondition(index)}
                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <Trash size={16} />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    <button onClick={addCondition} className="w-full py-2.5 mt-2 bg-blue-400 bg-opacity-30 text-blue-700 rounded-lg text-sm font-medium">
                      + Add Condition
                    </button>
                  </div>
                </div>
              )}

              {/* Stocks Section */}
              {(formData.type === 'static' || (formData.type === 'dynamic' && formData.staticStocks.length > 0)) && (
                <div className="pt-4 border-t border-gray-100 space-y-4">
                  <div className="flex items-center gap-2">
                    <Pin size={18} className="text-gray-900" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {formData.type === 'dynamic' ? 'Current Stocks' : 'Select Stocks'}
                    </h3>
                  </div>
                  <MultiSelect
                    value={formData.staticStocks}
                    onChange={(values) => handleInputChange('staticStocks', values)}
                    placeholder={formData.type === 'dynamic' ? '' : 'Search symbols...'}
                    disabled={formData.type === 'dynamic'}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <NeutralButton
            icon={Trash}
            label={deleteLoading ? "Deleting..." : "Delete"}
            textColor={"red"}
            className="hover:text-white hover:bg-red-500"
            onClick={handleDelete}
            disabled={deleteLoading}
          />
          <div className="flex gap-3">
            <button onClick={onClose} className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saveLoading || loading}
              className={`px-6 py-2 rounded-lg text-sm font-medium ${saveLoading ? 'bg-gray-300 text-gray-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            >
              {saveLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatchlistDetailModal;
